import {
	Autocomplete,
	Button,
	Chip,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import constants from '../../constants';
// GH-697: the offers table (+ popups) and its row types moved to SubsidyOffersTable, shared with the cross-company page.
import SubsidyOffersTable, { OfferRow, OfferStatus } from './SubsidyOffersTable';

interface SubsidyConfig {
	_id?: string;
	company?: string;
	enabled: boolean;
	annual_budget: number;
	cycle_start_date: string;
	cooldown_months: number;
	offer_timeout_days: number;
	tier_prices: { intro: number; full_treatment?: number; series?: number };
	tier_threshold: number;
	priority_member_emails: string[];
	/** "עדיפות נמוכה" — in practice never offered (Uri, 16.9.2026) */
	low_priority_member_emails: string[];
	priority_departments: string[];
	paused: boolean;
}

interface CycleData {
	year: number;
	budget_total: number;
	spent: number;
	committed: number;
	remaining: number;
	offers_count: number;
	accepted_count: number;
	declined_count: number;
	expired_count: number;
	paid_at?: string | null;
}

const SubsidyManagement = () => {
	const record = useRecordContext();
	const notify = useNotify();
	const companyId = record?._id || record?.id;

	const [config, setConfig] = useState<SubsidyConfig | null>(null);
	const [cycle, setCycle] = useState<CycleData | null>(null);
	const [offers, setOffers] = useState<OfferRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [statusFilter, setStatusFilter] = useState<OfferStatus | 'all'>('all');

	useEffect(() => {
		if (!companyId) return;
		(async () => {
			setLoading(true);
			try {
				const [cfgRes, cycleRes, offersRes] = await Promise.all([
					axios.get(`${constants.API_URL}/company-subsidy/admin/${companyId}/config`),
					axios.get(`${constants.API_URL}/company-subsidy/admin/${companyId}/cycle`),
					axios.get(`${constants.API_URL}/company-subsidy/admin/${companyId}/offers?limit=500`),
				]);
				const cfg = (cfgRes.data ?? {}) as Partial<SubsidyConfig>;
				const merged: SubsidyConfig = {
					enabled: cfg.enabled ?? false,
					annual_budget: cfg.annual_budget ?? 0,
					cycle_start_date:
						(cfg.cycle_start_date && String(cfg.cycle_start_date).slice(0, 10)) ||
						new Date().toISOString().slice(0, 10),
					cooldown_months: cfg.cooldown_months ?? 3,
					offer_timeout_days: cfg.offer_timeout_days ?? 7,
					tier_prices: { intro: 210, full_treatment: 420, ...(cfg.tier_prices || {}) },
					tier_threshold: cfg.tier_threshold ?? 60,
					priority_member_emails: cfg.priority_member_emails ?? [],
					low_priority_member_emails: cfg.low_priority_member_emails ?? [],
					priority_departments: cfg.priority_departments ?? [],
					paused: cfg.paused ?? false,
				};
				setConfig(merged);
				if (cycleRes.data) setCycle(cycleRes.data as CycleData);
				if (offersRes.data && Array.isArray(offersRes.data.docs)) {
					setOffers(offersRes.data.docs as OfferRow[]);
				}
			} catch (e: any) {
				notify(e?.response?.data?.message || 'טעינת ההגדרות נכשלה', { type: 'error' });
			} finally {
				setLoading(false);
			}
		})();
	}, [companyId, notify]);

	const aggregates = useMemo(() => {
		// GH-796 — demo offers never count in the company's budget numbers (the backend already leaves them out
		// of this list by default; this is defence in depth).
		const real = offers.filter((o) => !o.is_demo);
		if (!real.length) return null;
		const total = real.length;
		const counts = { offered: 0, accepted: 0, declined: 0, expired: 0, cancelled: 0 } as Record<OfferStatus, number>;
		let committed = 0;
		let spent = 0;
		for (const o of real) {
			counts[o.status] += 1;
			if (o.status === 'offered') committed += o.price;
			if (o.status === 'accepted') spent += o.price;
		}
		return { total, counts, committed, spent };
	}, [offers]);

	const filteredOffers = useMemo(() => {
		if (statusFilter === 'all') return offers;
		return offers.filter((o) => o.status === statusFilter);
	}, [offers, statusFilter]);

	const save = async () => {
		if (!config || !companyId) return;
		setSaving(true);
		try {
			await axios.put(`${constants.API_URL}/company-subsidy/admin/${companyId}/config`, config);
			notify('ההגדרות נשמרו', { type: 'success' });
		} catch (e: any) {
			notify(e?.response?.data?.message || 'שמירת ההגדרות נכשלה', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	// Manual trigger removed — the daily cron now handles batch creation automatically
	// once cycle_start_date is reached and the 25-day window has passed.

	const togglePaid = async () => {
		if (!companyId) return;
		const nextPaid = !cycle?.paid_at;
		if (nextPaid && !window.confirm('לסמן את המחזור הנוכחי כשולם? אחרי זה התקציב ותאריך ההתחלה ינעלו לעריכה.')) return;
		if (!nextPaid && !window.confirm('להסיר סימון "שולם"? התקציב יחזור להיות פתוח לעריכה.')) return;
		try {
			const res = await axios.post(`${constants.API_URL}/company-subsidy/admin/${companyId}/mark-paid`, {
				paid: nextPaid,
			});
			setCycle(res.data as CycleData);
			notify(nextPaid ? 'סומן כשולם' : 'הוסר סימון התשלום', { type: 'success' });
		} catch (e: any) {
			notify(e?.response?.data?.message || 'הפעולה נכשלה', { type: 'error' });
		}
	};

	// Trigger an immediate distribution now (instead of waiting for the weekly cron).
	const runNow = async () => {
		if (!companyId) return;
		if (!window.confirm('להפעיל חלוקת הצעות עכשיו לחברה זו? (במקום להמתין ל-cron השבועי)')) return;
		try {
			const res = await axios.post(`${constants.API_URL}/company-subsidy/admin/${companyId}/run-now`, {});
			const per = res.data?.perCompany?.[0];
			const created = per?.offersCreated ?? res.data?.offersCreated ?? 0;
			if (per?.skipped) {
				notify(`לא נוצרו הצעות (${per.skipped})`, { type: 'warning' });
			} else {
				notify(`נוצרו ${created} הצעות`, { type: 'success' });
			}
		} catch (e: any) {
			notify(e?.response?.data?.message || 'ההפעלה נכשלה', { type: 'error' });
		}
	};

	if (loading || !config) {
		return <Typography sx={{ p: 3 }}>טוען…</Typography>;
	}

	return (
		<Grid container spacing={3} sx={{ p: 2, direction: 'rtl' }}>
			{/* Master controls */}
			<Grid item xs={12}>
				<Paper sx={{ p: 3 }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">הפעלת השירות</Typography>
						<FormControlLabel
							control={
								<Switch
									checked={config.enabled}
									onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
								/>
							}
							label={config.enabled ? 'השירות פעיל' : 'השירות כבוי'}
						/>
					</Stack>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						הפעלה כאן חושפת ל-HR את עמוד ניהול התקציב. שדות עסקיים (מחירי הפגישות וסף הצורך) נשלטים מטאב זה.
					</Typography>
				</Paper>
			</Grid>

			{/* Annual budget + start date + payment lock */}
			<Grid item xs={12} md={6}>
				<Paper sx={{ p: 3, height: '100%' }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
						<Typography variant="h6">תקציב שנתי ותחילת מחזור</Typography>
						{cycle?.paid_at ? (
							<Chip
								size="small"
								color="success"
								label={`שולם — ${new Date(cycle.paid_at).toLocaleDateString('he-IL')}`}
							/>
						) : (
							<Chip size="small" color="warning" label="טרם שולם" />
						)}
					</Stack>
					<TextField
						label="תקציב שנתי (₪)"
						type="number"
						fullWidth
						value={config.annual_budget}
						disabled={!!cycle?.paid_at}
						onChange={(e) =>
							setConfig({ ...config, annual_budget: Number(e.target.value) || 0 })
						}
						sx={{ mb: 2 }}
						helperText={
							cycle?.paid_at
								? 'התקציב נעול עד תום המחזור (12 חודשים מתאריך תחילת המחזור).'
								: 'הסכום הכולל למחזור של 12 חודשים מתאריך תחילת המחזור.'
						}
					/>
					<TextField
						label="תחילת מחזור"
						type="date"
						fullWidth
						value={config.cycle_start_date.slice(0, 10)}
						disabled={!!cycle?.paid_at}
						onChange={(e) => setConfig({ ...config, cycle_start_date: e.target.value })}
						InputLabelProps={{ shrink: true }}
						helperText="המחזור הוא 12 חודשים מהתאריך הזה (לא שנה קלנדרית)."
						sx={{ mb: 2 }}
					/>
					<Stack direction="row" spacing={1} alignItems="center">
						<Button
							size="small"
							variant={cycle?.paid_at ? 'outlined' : 'contained'}
							color={cycle?.paid_at ? 'warning' : 'primary'}
							onClick={togglePaid}
						>
							{cycle?.paid_at ? 'הסר סימון תשלום' : 'סמן כשולם'}
						</Button>
						{cycle?.paid_at && (
							<Button size="small" variant="contained" color="success" onClick={runNow}>
								הפעל חלוקה עכשיו
							</Button>
						)}
					</Stack>
					{cycle?.paid_at && (
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
							מחלק הצעות מיד עד גובה התקציב הזמין, במקום להמתין ל-cron השבועי (ראשון 09:00).
						</Typography>
					)}
				</Paper>
			</Grid>

			{/* Business config — two tier prices */}
			<Grid item xs={12} md={6}>
				<Paper sx={{ p: 3, height: '100%' }}>
					<Typography variant="h6" sx={{ mb: 2 }}>
						תמחור עסקי (שני סוגי פגישות)
					</Typography>
					<TextField
						label="מחיר פגישה ממוקדת עם מומחה (₪)"
						type="number"
						fullWidth
						disabled
						value={config.tier_prices.intro}
						InputProps={{ readOnly: true }}
						sx={{ mb: 2 }}
						helperText="הפגישה ה'זולה' — מוצעת לעובדים בצורך נמוך מהסף. ערך קבוע: 210₪."
					/>
					<TextField
						label="מחיר פגישה מלאה עם מומחה (₪)"
						type="number"
						fullWidth
						disabled
						value={config.tier_prices.full_treatment ?? 420}
						InputProps={{ readOnly: true }}
						helperText="הפגישה ה'מלאה' — מוצעת לעובדים בצורך מעל הסף. ערך קבוע: 420₪."
					/>
				</Paper>
			</Grid>

			<Grid item xs={12} md={6}>
				<Paper sx={{ p: 3, height: '100%' }}>
					<Typography variant="h6" sx={{ mb: 2 }}>
						סף הצורך לבחירת סוג פגישה
					</Typography>
					<TextField
						label="סף ציון צורך (0–100)"
						type="number"
						fullWidth
						value={config.tier_threshold}
						onChange={(e) =>
							setConfig({
								...config,
								tier_threshold: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
							})
						}
						inputProps={{ min: 0, max: 100 }}
						helperText="⚠️ לא בשימוש במודל האדפטיבי (חלוקת אחוזים): הדירוג קובע מי מקבל מלא ומי מיקוד. נשמר לתאימות בלבד."
					/>
					<TextField
						label="תקופת המתנה בין הטבות (חודשים)"
						type="number"
						fullWidth
						value={config.cooldown_months}
						onChange={(e) =>
							setConfig({
								...config,
								cooldown_months: Math.max(1, Math.min(6, Number(e.target.value) || 1)),
							})
						}
						inputProps={{ min: 1, max: 6 }}
						sx={{ mt: 2 }}
						helperText="לאחר קבלת הטבה, עובד לא ייכלל בהקצאות חדשות למשך תקופה זו. ברירת מחדל: 3 חודשים."
					/>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						התקציב נפתח רבעון אחר רבעון (¼ מהתקציב השנתי בכל רבעון), ויתרה שלא נוצלה מתגלגלת לרבעון הבא.
					</Typography>
				</Paper>
			</Grid>

			{/* Priority lists (same lists HR edits on the budget page) */}
			<Grid item xs={12} md={6}>
				<Paper sx={{ p: 3, height: '100%' }}>
					<Typography variant="h6" sx={{ mb: 1 }}>
						עובדים בעדיפות גבוהה
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						נכנסים לראש התור בהקצאה, בלי קשר לציון הצורך.
					</Typography>
					<Autocomplete
						multiple
						freeSolo
						options={[]}
						value={config.priority_member_emails}
						onChange={(_, v) => setConfig({ ...config, priority_member_emails: v.map((x) => String(x).trim().toLowerCase()).filter(Boolean) })}
						renderInput={(p) => <TextField {...p} label="כתובות מייל (Enter אחרי כל אחת)" />}
					/>
				</Paper>
			</Grid>
			<Grid item xs={12} md={6}>
				<Paper sx={{ p: 3, height: '100%' }}>
					<Typography variant="h6" sx={{ mb: 1 }}>
						עובדים בעדיפות נמוכה
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						עובדים ברשימה הזו מקבלים עדיפות נמוכה בהקצאה (בפועל: לא מוצעים, ההגדרה מנוסחת ל-HR כ"עדיפות נמוכה").
					</Typography>
					<Autocomplete
						multiple
						freeSolo
						options={[]}
						value={config.low_priority_member_emails ?? []}
						onChange={(_, v) => setConfig({ ...config, low_priority_member_emails: v.map((x) => String(x).trim().toLowerCase()).filter(Boolean) })}
						renderInput={(p) => <TextField {...p} label="כתובות מייל (Enter אחרי כל אחת)" />}
					/>
				</Paper>
			</Grid>

			{/* Action bar */}
			<Grid item xs={12}>
				<Button variant="contained" onClick={save} disabled={saving}>
					{saving ? 'שומר…' : 'שמור הגדרות עסקיות'}
				</Button>
			</Grid>

			{/* Aggregate summary */}
			{aggregates && (
				<Grid item xs={12}>
					<Paper sx={{ p: 3 }}>
						<Typography variant="h6" sx={{ mb: 2 }}>
							סיכום מחזור
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={6} md={3}>
								<Typography variant="body2" color="text.secondary">
									סה״כ הצעות
								</Typography>
								<Typography variant="h5">{aggregates.total}</Typography>
							</Grid>
							<Grid item xs={6} md={3}>
								<Typography variant="body2" color="text.secondary">
									נוצל
								</Typography>
								<Typography variant="h5">₪{aggregates.spent.toLocaleString()}</Typography>
							</Grid>
							<Grid item xs={6} md={3}>
								<Typography variant="body2" color="text.secondary">
									הוקצה
								</Typography>
								<Typography variant="h5">₪{aggregates.committed.toLocaleString()}</Typography>
							</Grid>
							<Grid item xs={6} md={3}>
								<Typography variant="body2" color="text.secondary">
									התקבלו
								</Typography>
								<Typography variant="h5">
									{aggregates.counts.accepted} / {aggregates.total}
								</Typography>
							</Grid>
						</Grid>
					</Paper>
				</Grid>
			)}

			{/* Identified offer history — admin-only */}
			<Grid item xs={12}>
				<Paper sx={{ p: 3 }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
						<Typography variant="h6">היסטוריית הצעות (מזהה אישי — לעיני אדמין בלבד)</Typography>
						<FormControl size="small" sx={{ minWidth: 150 }}>
							<InputLabel>סטטוס</InputLabel>
							<Select
								value={statusFilter}
								label="סטטוס"
								onChange={(e) => setStatusFilter(e.target.value as OfferStatus | 'all')}
							>
								<MenuItem value="all">הכל</MenuItem>
								<MenuItem value="offered">בהמתנה</MenuItem>
								<MenuItem value="accepted">התקבלה</MenuItem>
								<MenuItem value="declined">נדחתה</MenuItem>
								<MenuItem value="expired">פקעה</MenuItem>
							</Select>
						</FormControl>
					</Stack>
					<SubsidyOffersTable offers={filteredOffers} maxRows={200} layout="compact" />
				</Paper>
			</Grid>

		</Grid>
	);
};

export default SubsidyManagement;
